import { useRef } from 'react';
import {
  ActionIcon,
  Anchor,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconFile, IconPaperclip, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../api/client';
import { api, rawRequest, unwrap } from '../api/client';
import type { Attachment } from '../api/types';
import { notifyError, notifySaved } from '../lib/notify';

/** The models the API accepts attachments for, kept in step with `ATTACHABLE_MODELS`. */
export type AttachmentTarget =
  | 'maintenance.maintenanceevent'
  | 'maintenance.maintenancecomment'
  | 'maintenance.maintenanceprogramme'
  | 'aircraft.aircraft'
  | 'aircraft.aircraftcomponent'
  | 'staff.person'
  | 'logbook.flightrecord'
  | 'organisations.organisation';

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentList({
  target,
  targetId,
  canEdit = true,
  compact = false,
}: {
  target: AttachmentTarget;
  targetId: number;
  canEdit?: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const queryKey = ['attachments', target, targetId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/attachments/', {
          params: { query: { target, target_id: targetId } },
        }),
      ),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file);
      body.append('target', target);
      body.append('target_id', String(targetId));
      // Multipart bodies go outside the typed client, which only speaks JSON.
      await rawRequest('/attachments/', { method: 'POST', body });
    },
    onSuccess: async () => {
      notifySaved(t('action.upload'));
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: ApiError) => notifyError(error),
  });

  const remove = useMutation({
    mutationFn: async (id: number) =>
      unwrap(await api.DELETE('/api/v1/attachments/{id}/', { params: { path: { id } } })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: ApiError) => notifyError(error),
  });

  const attachments = (data?.results ?? []) as Attachment[];

  return (
    <Stack gap="xs">
      {isLoading ? (
        <Loader size="xs" />
      ) : (
        attachments.map((attachment) => (
          <Paper key={attachment.id} p="xs" radius="sm">
            <Group justify="space-between" wrap="nowrap" gap="xs">
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <IconFile size={16} opacity={0.6} />
                <Anchor
                  href={attachment.file}
                  target="_blank"
                  rel="noreferrer"
                  fz="sm"
                  truncate="end"
                >
                  {attachment.title || attachment.filename}
                </Anchor>
                <Text fz="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {formatSize(attachment.size_bytes)}
                </Text>
              </Group>
              {canEdit ? (
                <Tooltip label={t('action.delete')}>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    loading={remove.isPending}
                    onClick={() => remove.mutate(attachment.id)}
                  >
                    <IconTrash size={15} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
            </Group>
          </Paper>
        ))
      )}

      {canEdit ? (
        <>
          <input
            ref={fileInput}
            type="file"
            hidden
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              if (file) upload.mutate(file);
              event.currentTarget.value = '';
            }}
          />
          <Button
            variant="light"
            size={compact ? 'compact-sm' : 'sm'}
            leftSection={<IconPaperclip size={15} />}
            loading={upload.isPending}
            onClick={() => fileInput.current?.click()}
            style={{ alignSelf: 'flex-start' }}
          >
            {t('action.upload')}
          </Button>
        </>
      ) : null}
    </Stack>
  );
}
